import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import PortfolioPieceEditor from "@/components/PortfolioPieceEditor";
import { toast } from "sonner";
import { Upload, FileText, Plus, X, Check, AlertTriangle, Globe } from "lucide-react";

const DISCIPLINES = [
  "graphic_designer", "copywriter", "art_director", "photographer",
  "ux_designer", "illustrator", "brand_designer", "other",
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(0); // 0: choose, 1: review, 2: portfolio, 3: writing, 4: prefs
  const [profile, setProfile] = useState(null);
  const [parsedPreview, setParsedPreview] = useState(null);
  const [lowConfidence, setLowConfidence] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { (async () => {
    const res = await api.get("/profile");
    setProfile(res.data);
  })(); }, []);

  if (!profile) return <Layout><div className="p-12 text-center text-muted-foreground text-xs uppercase tracking-[0.3em]">LOADING<span className="blink-caret"/></div></Layout>;

  const updateProfile = async (patch) => {
    const res = await api.put("/profile", patch);
    setProfile(res.data);
    return res.data;
  };

  const handleResumeUpload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload/resume", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setParsedPreview(res.data.parsed);
      setLowConfidence(res.data.low_confidence_fields || []);
      const patch = { ...res.data.parsed };
      delete patch.low_confidence_fields;
      const updated = await updateProfile(patch);
      setProfile(updated);
      setStep(1);
      toast.success("Resume parsed. Review fields below.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Resume parsing failed");
    } finally {
      setBusy(false);
    }
  };

  const handleUrlScrape = async (url) => {
    setBusy(true);
    try {
      const res = await api.post("/upload/portfolio-url", { url });
      const p = res.data.parsed || {};
      // Merge with current profile (don't blow away existing data)
      const merged = {};
      if (p.name && !profile.name) merged.name = p.name;
      if (p.title && !profile.title) merged.title = p.title;
      if (p.bio && !profile.bio) merged.bio = p.bio;
      if (p.discipline && !profile.discipline) merged.discipline = p.discipline;
      if (p.skills?.length && (!profile.skills || profile.skills.length === 0)) merged.skills = p.skills;
      // Always APPEND scraped portfolio pieces
      if (p.portfolio_pieces?.length) {
        merged.portfolio_pieces = [...(profile.portfolio_pieces || []), ...p.portfolio_pieces];
      }
      setLowConfidence(res.data.low_confidence_fields || []);
      const updated = await updateProfile(merged);
      setProfile(updated);
      setStep(1);
      toast.success(`Imported ${p.portfolio_pieces?.length || 0} pieces from ${res.data.site_title || res.data.scraped_url}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "URL scrape failed");
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    setBusy(true);
    const updated = await updateProfile({});
    if (updated.complete) {
      await refresh();
      toast.success("Profile complete. Generating your feed.");
      navigate("/dashboard");
    } else {
      toast.warning("Some required fields are still missing: " + updated.gaps.join(", "));
    }
    setBusy(false);
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-5 md:px-12 py-6 sm:py-12">
        <div className="terminal-label mb-2 text-primary">[ ONBOARDING / STEP {step + 1}/6 ]</div>
        <h1 className="font-display text-2xl sm:text-3xl md:text-4xl uppercase font-bold tracking-tight mb-2">
          Build your profile
        </h1>
        <p className="text-sm text-muted-foreground mb-6 sm:mb-10 max-w-2xl">
          Sisyphean uses your profile to tailor every application. Upload your existing resume or fill it in manually. Both work the same way.
        </p>

        {/* Step indicator - horizontal scroll on mobile */}
        <div className="flex md:grid md:grid-cols-6 gap-px mb-6 sm:mb-10 border border-border overflow-x-auto md:overflow-visible -mx-5 px-5 md:mx-0 md:px-0">
          {["Source", "Review", "Portfolio", "Voice", "Preferences", "Email"].map((label, i) => (
            <button
              key={label}
              data-testid={`onboarding-step-${i}`}
              onClick={() => setStep(i)}
              className={`flex-shrink-0 md:flex-shrink p-3 text-[10px] uppercase tracking-[0.18em] transition-colors whitespace-nowrap ${
                step === i ? "bg-primary text-primary-foreground" : i < step ? "bg-surface text-foreground" : "bg-background text-muted-foreground"
              }`}
            >
              {i + 1} / {label}
            </button>
          ))}
        </div>

        {step === 0 && <StepSource onUpload={handleResumeUpload} onUrlScrape={handleUrlScrape} onManual={() => setStep(1)} busy={busy} />}
        {step === 1 && <StepReview profile={profile} setProfile={setProfile} updateProfile={updateProfile} lowConfidence={lowConfidence} onNext={() => setStep(2)} />}
        {step === 2 && <StepPortfolio profile={profile} setProfile={setProfile} onNext={() => setStep(3)} />}
        {step === 3 && <StepWriting profile={profile} setProfile={setProfile} onNext={() => setStep(4)} />}
        {step === 4 && <StepPreferences profile={profile} updateProfile={updateProfile} onNext={() => setStep(5)} busy={busy} />}
        {step === 5 && <StepEmailPrefs onFinish={finish} busy={busy} />}
      </div>
    </Layout>
  );
}

function StepSource({ onUpload, onUrlScrape, onManual, busy }) {
  const [url, setUrl] = useState("");
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="brut-tile p-8 hover:border-primary cursor-pointer block transition-colors" data-testid="onboarding-upload-resume">
          <input type="file" accept=".pdf,.docx,.txt" className="hidden" onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])} disabled={busy} />
          <Upload className="w-7 h-7 text-primary mb-4" strokeWidth={1.5} />
          <h3 className="text-lg font-display font-semibold mb-2">Upload Resume</h3>
          <p className="text-xs text-muted-foreground mb-4">PDF, DOCX, or TXT. Parsed by Claude with low-confidence flagging.</p>
          <div className="text-[10px] uppercase tracking-[0.2em] text-primary">{busy ? "Parsing..." : "Click to upload →"}</div>
        </label>

        <button onClick={onManual} className="brut-tile p-8 hover:border-primary text-left transition-colors" data-testid="onboarding-manual-entry">
          <FileText className="w-7 h-7 text-primary mb-4" strokeWidth={1.5} />
          <h3 className="text-lg font-display font-semibold mb-2">Enter Manually</h3>
          <p className="text-xs text-muted-foreground mb-4">Fill in work history, skills, education through a structured form.</p>
          <div className="text-[10px] uppercase tracking-[0.2em] text-primary">Skip to form →</div>
        </button>

        <div className="brut-tile p-8" data-testid="onboarding-url-tile">
          <Globe className="w-7 h-7 text-primary mb-4" strokeWidth={1.5} />
          <h3 className="text-lg font-display font-semibold mb-2">Scan Portfolio URL</h3>
          <p className="text-xs text-muted-foreground mb-4">We crawl your existing site and extract pieces, bio, and skills.</p>
          <input
            data-testid="onboarding-url-input"
            className="input-base text-xs mb-3"
            placeholder="yourname.com or behance.net/you"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={busy}
          />
          <button
            data-testid="onboarding-url-scrape"
            onClick={() => url.trim() && onUrlScrape(url.trim())}
            disabled={busy || !url.trim()}
            className="btn-primary w-full text-[10px]"
          >
            {busy ? "Scanning..." : "Scan site →"}
          </button>
        </div>
      </div>
      <p className="terminal-label text-center pt-4">// You can mix any of these. Uploaded data stacks with manual entries.</p>
    </div>
  );
}

function FieldInput({ label, value, onChange, low, ...rest }) {
  return (
    <div>
      <label className="terminal-label flex items-center gap-2">
        {label}
        {low && <AlertTriangle className="w-3 h-3 text-accent-amber" />}
      </label>
      <input className={`input-base mt-2 ${low ? "border-accent-amber" : ""}`} value={value || ""} onChange={(e) => onChange(e.target.value)} {...rest} />
      {low && <div className="text-[10px] uppercase tracking-[0.2em] text-accent-amber mt-1">LOW CONFIDENCE - VERIFY</div>}
    </div>
  );
}

function StepReview({ profile, setProfile, updateProfile, lowConfidence, onNext }) {
  const [local, setLocal] = useState({
    name: profile.name, title: profile.title, location: profile.location,
    discipline: profile.discipline, bio: profile.bio,
    skills: (profile.skills || []).join(", "),
    work_history: profile.work_history?.length ? profile.work_history : [{ company: "", role: "", start: "", end: "", description: "" }],
    education: profile.education?.length ? profile.education : [{ school: "", degree: "", year: "" }],
  });

  const isLow = (f) => lowConfidence.includes(f);

  const save = async () => {
    const patch = {
      ...local,
      skills: local.skills.split(",").map((s) => s.trim()).filter(Boolean),
      work_history: local.work_history.filter((w) => w.company || w.role),
      education: local.education.filter((e) => e.school || e.degree),
    };
    const res = await updateProfile(patch);
    setProfile(res);
    onNext();
  };

  return (
    <div className="panel p-8 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FieldInput label="Name" value={local.name} onChange={(v) => setLocal({ ...local, name: v })} low={isLow("name")} data-testid="profile-name" />
        <FieldInput label="Title" value={local.title} onChange={(v) => setLocal({ ...local, title: v })} low={isLow("title")} data-testid="profile-title" placeholder="Senior Brand Designer" />
        <FieldInput label="Location" value={local.location} onChange={(v) => setLocal({ ...local, location: v })} low={isLow("location")} data-testid="profile-location" />
        <div>
          <label className="terminal-label">Discipline</label>
          <select data-testid="profile-discipline" className="input-base mt-2" value={local.discipline || ""} onChange={(e) => setLocal({ ...local, discipline: e.target.value })}>
            <option value="">-- select --</option>
            {DISCIPLINES.map((d) => <option key={d} value={d}>{d.replace(/_/g, " ")}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="terminal-label">Bio</label>
        <textarea data-testid="profile-bio" className="input-base mt-2 min-h-[80px]" value={local.bio || ""} onChange={(e) => setLocal({ ...local, bio: e.target.value })} placeholder="One short paragraph on what you do." />
      </div>
      <div>
        <label className="terminal-label">Skills (comma-separated)</label>
        <input data-testid="profile-skills" className="input-base mt-2" value={local.skills} onChange={(e) => setLocal({ ...local, skills: e.target.value })} placeholder="Brand identity, typography, art direction, ..." />
      </div>

      <div>
        <label className="terminal-label mb-3 block">Work history</label>
        <div className="space-y-3">
          {local.work_history.map((w, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 panel p-4">
              <input className="input-base md:col-span-3" placeholder="Company" value={w.company} onChange={(e) => { const a = [...local.work_history]; a[i] = { ...w, company: e.target.value }; setLocal({ ...local, work_history: a }); }} />
              <input className="input-base md:col-span-3" placeholder="Role" value={w.role} onChange={(e) => { const a = [...local.work_history]; a[i] = { ...w, role: e.target.value }; setLocal({ ...local, work_history: a }); }} />
              <input className="input-base md:col-span-2" placeholder="Start" value={w.start} onChange={(e) => { const a = [...local.work_history]; a[i] = { ...w, start: e.target.value }; setLocal({ ...local, work_history: a }); }} />
              <input className="input-base md:col-span-2" placeholder="End" value={w.end} onChange={(e) => { const a = [...local.work_history]; a[i] = { ...w, end: e.target.value }; setLocal({ ...local, work_history: a }); }} />
              <button className="md:col-span-2 btn-secondary" onClick={() => setLocal({ ...local, work_history: local.work_history.filter((_, j) => j !== i) })}><X className="w-3 h-3 inline" /> Remove</button>
              <textarea className="input-base md:col-span-12 min-h-[60px]" placeholder="Description / achievements" value={w.description} onChange={(e) => { const a = [...local.work_history]; a[i] = { ...w, description: e.target.value }; setLocal({ ...local, work_history: a }); }} />
            </div>
          ))}
          <button data-testid="add-work-history" className="btn-secondary inline-flex items-center gap-2" onClick={() => setLocal({ ...local, work_history: [...local.work_history, { company: "", role: "", start: "", end: "", description: "" }] })}>
            <Plus className="w-3 h-3" /> Add role
          </button>
        </div>
      </div>

      <div>
        <label className="terminal-label mb-3 block">Education</label>
        <div className="space-y-3">
          {local.education.map((ed, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 panel p-4">
              <input className="input-base md:col-span-5" placeholder="School" value={ed.school} onChange={(e) => { const a = [...local.education]; a[i] = { ...ed, school: e.target.value }; setLocal({ ...local, education: a }); }} />
              <input className="input-base md:col-span-4" placeholder="Degree" value={ed.degree} onChange={(e) => { const a = [...local.education]; a[i] = { ...ed, degree: e.target.value }; setLocal({ ...local, education: a }); }} />
              <input className="input-base md:col-span-2" placeholder="Year" value={ed.year} onChange={(e) => { const a = [...local.education]; a[i] = { ...ed, year: e.target.value }; setLocal({ ...local, education: a }); }} />
              <button className="md:col-span-1 btn-secondary px-2" onClick={() => setLocal({ ...local, education: local.education.filter((_, j) => j !== i) })}><X className="w-3 h-3" /></button>
            </div>
          ))}
          <button className="btn-secondary inline-flex items-center gap-2" onClick={() => setLocal({ ...local, education: [...local.education, { school: "", degree: "", year: "" }] })}>
            <Plus className="w-3 h-3" /> Add education
          </button>
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-border">
        <button data-testid="onboarding-review-next" onClick={save} className="btn-primary">Continue → Portfolio</button>
      </div>
    </div>
  );
}

function StepPortfolio({ profile, setProfile, onNext }) {
  const [pieces, setPieces] = useState(profile.portfolio_pieces || []);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const valid = pieces.filter((p) => p.title || p.description);
      const res = await api.put("/profile", { portfolio_pieces: valid });
      setProfile(res.data);
      onNext();
    } finally { setBusy(false); }
  };

  return (
    <div className="panel p-8 space-y-6">
      <div>
        <div className="terminal-label">[ PORTFOLIO.PIECES ]</div>
        <p className="text-sm text-muted-foreground mt-2">Add 3-10 representative pieces. Pick a type for each. Sisyphean renders them inline on every per-application portfolio.</p>
      </div>

      <PortfolioPieceEditor pieces={pieces} onChange={setPieces} dataTestidPrefix="onb-piece" />

      <div className="flex justify-end pt-4 border-t border-border">
        <button data-testid="onboarding-portfolio-next" onClick={save} disabled={busy} className="btn-primary">Continue → Voice</button>
      </div>
    </div>
  );
}

function StepWriting({ profile, setProfile, onNext }) {
  const [samples, setSamples] = useState(profile.writing_samples || []);
  const [voice, setVoice] = useState(profile.writing_voice_analysis || null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);

  const addSample = async () => {
    if (!content || content.length < 30) { toast.error("Need at least 30 characters"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", title || "Untitled");
      fd.append("content", content);
      const res = await api.post("/upload/writing-sample", fd);
      setSamples([...samples, res.data.sample]);
      setVoice(res.data.writing_voice_analysis);
      setTitle(""); setContent("");
      toast.success("Sample added. Voice updated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const removeSample = async (id) => {
    await api.delete(`/writing-sample/${id}`);
    setSamples(samples.filter((s) => s.id !== id));
  };

  return (
    <div className="panel p-8 space-y-6">
      <div>
        <div className="terminal-label">[ VOICE.SAMPLES / OPTIONAL ]</div>
        <p className="text-sm text-muted-foreground mt-2">Paste 1-3 samples of your writing: past cover letters, essays, bios, anything in your own words. We&apos;ll match your voice in cover letters.</p>
      </div>

      {samples.length > 0 && (
        <div className="space-y-2">
          {samples.map((s) => (
            <div key={s.id} className="panel p-4 flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="font-mono text-sm font-medium">{s.title}</div>
                <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{s.content.substring(0, 200)}</div>
              </div>
              <button onClick={() => removeSample(s.id)} className="text-muted-foreground hover:text-destructive"><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      {voice && (
        <div className="panel p-5 border-accent-green/40">
          <div className="terminal-label text-accent-green mb-3">[ VOICE.ANALYSIS ]</div>
          <div className="text-sm space-y-1 font-mono">
            <div><span className="text-muted-foreground">tone:</span> {voice.tone}</div>
            <div><span className="text-muted-foreground">vocab:</span> {voice.vocabulary}</div>
            <div><span className="text-muted-foreground">cadence:</span> {voice.sentence_structure}</div>
            <div className="pt-2 text-foreground/80">{voice.voice_summary}</div>
          </div>
        </div>
      )}

      <div className="panel p-5">
        <input data-testid="writing-sample-title" className="input-base mb-3" placeholder="Title (e.g., Old cover letter)" value={title} onChange={(e) => setTitle(e.target.value)} />
        <textarea data-testid="writing-sample-content" className="input-base min-h-[160px] mb-3" placeholder="Paste your writing here..." value={content} onChange={(e) => setContent(e.target.value)} />
        <button data-testid="add-writing-sample" onClick={addSample} disabled={busy} className="btn-primary">Analyze sample</button>
      </div>

      <div className="flex justify-between pt-4 border-t border-border">
        <button onClick={onNext} className="btn-ghost">Skip →</button>
        <button data-testid="onboarding-writing-next" onClick={onNext} className="btn-primary">Continue → Preferences</button>
      </div>
    </div>
  );
}

function StepPreferences({ profile, updateProfile, onNext, busy }) {
  const [prefs, setPrefs] = useState({
    desired_roles: (profile.preferences?.desired_roles || []).join(", "),
    industries: (profile.preferences?.industries || []).join(", "),
    work_modes: profile.preferences?.work_modes || [],
    enjoy_factors: profile.preferences?.enjoy_factors || "",
    avoid_factors: profile.preferences?.avoid_factors || "",
  });

  const toggle = (key, val) => {
    const arr = prefs[key].includes(val) ? prefs[key].filter((x) => x !== val) : [...prefs[key], val];
    setPrefs({ ...prefs, [key]: arr });
  };

  const save = async () => {
    await updateProfile({
      preferences: {
        desired_roles: prefs.desired_roles.split(",").map((s) => s.trim()).filter(Boolean),
        industries: prefs.industries.split(",").map((s) => s.trim()).filter(Boolean),
        work_modes: prefs.work_modes,
        enjoy_factors: prefs.enjoy_factors,
        avoid_factors: prefs.avoid_factors,
        company_sizes: [],
      },
    });
    onNext();
  };

  return (
    <div className="panel p-8 space-y-6">
      <div>
        <div className="terminal-label">[ JOB.PREFERENCES ]</div>
        <p className="text-sm text-muted-foreground mt-2">Tell Sisyphean what you want. We use this to score how much you&apos;d <em>enjoy</em> each role.</p>
      </div>

      <div>
        <label className="terminal-label">Desired roles (comma-separated)</label>
        <input data-testid="prefs-roles" className="input-base mt-2" value={prefs.desired_roles} onChange={(e) => setPrefs({ ...prefs, desired_roles: e.target.value })} placeholder="Brand Designer, Art Director, ..." />
      </div>
      <div>
        <label className="terminal-label">Industries you&apos;d like to work in</label>
        <input data-testid="prefs-industries" className="input-base mt-2" value={prefs.industries} onChange={(e) => setPrefs({ ...prefs, industries: e.target.value })} placeholder="Publishing, Music, Hospitality, ..." />
      </div>
      <div>
        <label className="terminal-label">Work mode</label>
        <div className="flex gap-2 mt-2 flex-wrap">
          {["remote", "hybrid", "onsite"].map((m) => (
            <button key={m} data-testid={`prefs-mode-${m}`} onClick={() => toggle("work_modes", m)} className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border ${prefs.work_modes.includes(m) ? "border-primary bg-primary text-primary-foreground" : "border-border text-foreground hover:border-primary"}`}>
              {m}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="terminal-label">Things you enjoy in a role</label>
        <textarea data-testid="prefs-enjoy" className="input-base mt-2 min-h-[80px]" value={prefs.enjoy_factors} onChange={(e) => setPrefs({ ...prefs, enjoy_factors: e.target.value })} placeholder="Idea-led work, small teams, room for craft, ..." />
      </div>
      <div>
        <label className="terminal-label">Things to avoid</label>
        <textarea data-testid="prefs-avoid" className="input-base mt-2 min-h-[80px]" value={prefs.avoid_factors} onChange={(e) => setPrefs({ ...prefs, avoid_factors: e.target.value })} placeholder="Pure production, corporate culture, ..." />
      </div>

      <div className="flex justify-end pt-4 border-t border-border">
        <button data-testid="onboarding-prefs-next" onClick={save} disabled={busy} className="btn-primary inline-flex items-center gap-2">
          Next: email preferences →
        </button>
      </div>
    </div>
  );
}


function StepEmailPrefs({ onFinish, busy }) {
  const CATEGORIES = [
    { key: "high_match_alerts", label: "High-match job alerts", desc: "Instant email when a 90%+ match lands on your feed." },
    { key: "weekly_digest", label: "Weekly digest", desc: "One email a week summarizing your top new matches." },
    { key: "feature_updates", label: "Feature updates", desc: "When we ship something new that affects your workflow." },
    { key: "announcements", label: "Announcements", desc: "Occasional milestones, press, and platform news." },
    { key: "tips_and_guides", label: "Tips and guides", desc: "Case studies + tactical advice on landing creative work." },
  ];
  const [prefs, setPrefs] = useState(Object.fromEntries(CATEGORIES.map((c) => [c.key, true])));
  const allOn = CATEGORIES.every((c) => prefs[c.key]);
  const allOff = CATEGORIES.every((c) => !prefs[c.key]);

  const toggle = (k) => setPrefs((p) => ({ ...p, [k]: !p[k] }));
  const setAll = (v) => setPrefs(Object.fromEntries(CATEGORIES.map((c) => [c.key, v])));

  const save = async () => {
    try {
      await api.put("/profile/email-preferences", prefs);
      onFinish();
    } catch (e) {
      toast.error("Couldn't save preferences. Try again.");
    }
  };

  return (
    <div className="panel p-8 space-y-6" data-testid="step-email-prefs">
      <div>
        <div className="terminal-label">[ EMAIL.PREFERENCES ]</div>
        <p className="text-sm text-muted-foreground mt-2">
          A la carte. Check only what you want to hear about, uncheck the rest. You can change these anytime from your profile, or unsubscribe with one click from any email.
        </p>
      </div>

      <div className="space-y-2">
        {CATEGORIES.map((c) => (
          <label key={c.key} className="flex items-start gap-3 p-3 border border-foreground/15 hover:border-foreground/40 cursor-pointer transition-colors" data-testid={`email-pref-${c.key}`}>
            <input
              type="checkbox"
              checked={prefs[c.key]}
              onChange={() => toggle(c.key)}
              className="mt-1 w-4 h-4 accent-primary flex-shrink-0"
            />
            <div className="min-w-0">
              <div className="text-sm font-medium">{c.label}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">{c.desc}</div>
            </div>
          </label>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
        <label className="flex items-center gap-3 cursor-pointer" data-testid="email-pref-all">
          <input
            type="checkbox"
            checked={allOn}
            ref={(el) => { if (el) el.indeterminate = !allOn && !allOff; }}
            onChange={() => setAll(!allOn)}
            className="w-4 h-4 accent-primary"
          />
          <span className="text-[11px] uppercase tracking-[0.22em] font-mono text-muted-foreground">All of the above</span>
        </label>
      </div>

      <div className="flex justify-end pt-4 border-t border-border">
        <button data-testid="onboarding-finish" onClick={save} disabled={busy} className="btn-primary inline-flex items-center gap-2">
          <Check className="w-4 h-4" /> Finish setup →
        </button>
      </div>
    </div>
  );
}
